import { IToolManifest, ICommand } from '../../../core/tools/interfaces/ITool';
import { TextEditTool } from '../../../core/tools/standard/TextEditTool';
import { Node } from '../../../core/models/scene';
import { ChangeNodeTextCommand } from '../commands/ChangeNodeTextCommand';
import i18n from '../../../i18n/config';
import { bindGenogramToolName } from './utils/toolNameI18n';

// Genogram-specific text editing tool
export class GenogramTextTool extends TextEditTool {
  manifest: IToolManifest;
  
  private nodes: Node[] = [];
  
  constructor() {
    super();
    
    // Build manifest from base and add genogram-specific properties
    this.manifest = {
      ...this.getBaseManifest(),
      id: 'genogram-text',
      name: i18n.t('genogram:tools.editPersonInfo', { defaultValue: 'Edit Person Info' }) as string,
      description: 'Edit person names and locations',
      supportedDiagramTypes: ['genogram'],
      supportedShapeTypes: ['person'],
      ui: { toolbar: true, contextMenu: true, propertyPanel: false }
    } as IToolManifest;

    bindGenogramToolName(this.manifest, 'tools.editPersonInfo', 'Edit Person Info');
  }
  
  setNodesAndUpdater(
    nodes: Node[], 
    _updateNode: (id: string, updates: Partial<Node>) => void
  ): void {
    this.nodes = nodes;
    // No longer store updateNode - commands handle mutations
  }
  
  getTextFields(): { name: string; label: string }[] {
    // Safe to use i18n.t here (non-hook, non-React environment)
    const nameLabel = i18n.t('common:labels.name', { defaultValue: 'Name' }) as string;
    const locationLabel = i18n.t('genogram:location.title', { defaultValue: 'Location' }) as string;
    return [
      { name: 'name', label: nameLabel },
      { name: 'location', label: locationLabel }
    ];
  }
  
  getItemText(itemId: string, field: string): string | undefined {
    const node = this.nodes.find(n => n.id === itemId);
    if (!node) return undefined;
    
    switch(field) {
      case 'name': return node.name;
      case 'location': return node.location;
      default: return undefined;
    }
  }
  
  setItemText(_itemId: string, _field: string, _text: string | undefined): void {
    // This method should not directly mutate state
    // The parent class will call execute() which returns a command
  }
  
  getSelectedItems(): string[] {
    if (!this.context) return [];
    
    const selectedNodeIds = Array.from(this.context.selection);
    
    // Filter to only person nodes
    return selectedNodeIds.filter(id => 
      this.nodes.find(n => n.id === id && n.type === 'person')
    );
  }
  
  // Override execute to return proper command
  execute(params: { text: string; field: string }): ICommand | null {
    if (!params.text || !params.field) return null;
    
    const selectedIds = this.getSelectedItems();
    if (selectedIds.length === 0) return null;
    
    return new ChangeNodeTextCommand(selectedIds, params.field, params.text);
  }
}
